import { Component, Inject, NgZone, OnDestroy, OnInit, Optional } from '@angular/core';
import { DateModel, DateRangeInterval } from '@app/core/components/date-range/date-range.component';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { Item } from '@app/core/components/listbox/listbox.component';
import { LayoutService } from '@app/core/services/layout.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ConsignmentService, ConsignmentsGet, ConsignmentsListParams, ConsignmentsParams } from '@app/services/consignment.service';
import { BusinesscoItem, LogisticService, StockplaceItem } from '@app/services/logistic.service';

@Component({
  selector: 'tt-consignments',
  templateUrl: './consignments.component.html',
  styleUrls: ['./consignments.component.css'],
  standalone: false,
})
export class ConsignmentsComponent implements OnInit, OnDestroy {
    public params?: ConsignmentsParams;
    public page?: ConsignmentsGet;
    public companies: BusinesscoItem[] = [];
    public stockplaces: StockplaceItem[] = [];
    public trolleys: Item[] = [];
    public shipments: Item[] = [];
    public consignments: Item[] = [];
    public grid?: GridOptions;
    public loading = false;
    public loadingList = false;
    public isDesktop: boolean;

    private readonly tabletQuery: MediaQueryList;
    private readonly intervals: DateRangeInterval[] = ['day', 'week', 'month', 'quarter', 'biannual', 'year', 'yeartodate', 'minmax'];
    private dependencyVersion = 0;
    private resultVersion = 0;
    private dependenciesLoading = true;
    private destroyed = false;
    private resultParams?: ConsignmentsListParams;

    private readonly onBreakpointChanged = (event: MediaQueryListEvent): void => {
        this.zone.run(() => {
            this.isDesktop = event.matches;
            void this.refreshResults();
        });
    };

    constructor(
        private consignment: ConsignmentService,
        private logistic: LogisticService,
        layout: LayoutService,
        private zone: NgZone,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
        this.tabletQuery = layout.getMediaQueries().tablet;
        this.isDesktop = this.tabletQuery.matches;
    }

    public get stockplaceRememberId(): string {
        return 'consignments.stockplace_id_from.businessco_no_from=' + (this.page?.businessco_no_from ?? '');
    }

    public get trolleyRememberId(): string {
        return 'w_consignments.stocklocation_keyno_trolley.stockplace_id=' + (this.page?.stockplace_id_from ?? '');
    }

    public get shipmentRememberId(): string {
        return 'w_consignments.consignmentshipment_keyno.stockplace_id=' + (this.page?.stockplace_id_from ?? '');
    }

    public get dateInterval(): DateRangeInterval {
        const interval = this.page?.dateselector_index ?? '';
        return this.intervals.find((value) => value === interval) ?? this.intervals[Number(interval) - 1] ?? 'day';
    }

    public async ngOnInit(): Promise<void> {
        this.tabletQuery.addEventListener('change', this.onBreakpointChanged);
        const params = this.navigator?.params;
        if (params?.['argtype'] == null || params['argvalue'] == null) return;
        this.params = { argtype: String(params['argtype']), argvalue: String(params['argvalue']) };
        this.loading = true;
        const initialVersion = this.dependencyVersion;
        try {
            const [page, companies] = await Promise.all([
                this.consignment.getConsignments(this.params), this.logistic.getCompanies(),
            ]);
            if (this.destroyed) return;
            this.page = page;
            this.companies = companies;
            if (page) await this.reloadDependencies(true);
        } finally {
            if (!this.destroyed && this.dependencyVersion === initialVersion) this.loading = false;
        }
    }

    public async onCompanyChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.businessco_no_from = value;
        await this.reloadDependencies(true);
    }

    public async onStockplaceChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.stockplace_id_from = value;
        await this.reloadDependencies(false);
    }

    private async reloadDependencies(resolveCompany: boolean): Promise<void> {
        if (!this.page) return;
        const version = ++this.dependencyVersion;
        ++this.resultVersion;
        this.loadingList = false;
        this.loading = true;
        this.dependenciesLoading = true;
        this.consignments = [];
        this.trolleys = [];
        this.shipments = [];
        try {
            const company = this.page.businessco_no_from;
            if (resolveCompany) {
                this.stockplaces = [];
                if (company?.trim()) {
                    const [stockplaces, defaults] = await Promise.all([
                        this.logistic.getStockplaces({ businessco_no: company }),
                        this.consignment.getCompanyChanged({ businessco_no_from: company }),
                    ]);
                    if (!this.isCurrentDependency(version) || !defaults) return;
                    this.stockplaces = stockplaces;
                    this.page.stockplace_id_from = defaults.stockplace_id_from;
                } else {
                    this.page.stockplace_id_from = '';
                }
            }
            const stockplace = this.page.stockplace_id_from;
            if (stockplace?.trim()) {
                const [defaults, trolleys, shipments] = await Promise.all([
                    this.consignment.getStockplaceChanged({ stockplace_id_from: stockplace }),
                    this.logistic.getTrolleys({ stockplace_id: stockplace, addall: '1' }),
                    this.consignment.getConsignmentShipments({ stockplace_id: stockplace, add_all: '1', add_none: '1' }),
                ]);
                if (!this.isCurrentDependency(version)) return;
                if (!defaults) return;
                this.page.stocklocation_keyno_trolley = defaults.stocklocation_keyno_trolley;
                this.page.consignmentshipment_keyno = defaults.consignmentshipment_keyno;
                this.trolleys = trolleys;
                this.shipments = shipments;
            } else {
                this.page.stocklocation_keyno_trolley = '0';
                this.page.consignmentshipment_keyno = '0';
            }
            this.dependenciesLoading = false;
            await this.refreshResults();
        } finally {
            if (this.isCurrentDependency(version)) {
                this.loading = false;
            }
        }
    }

    private isCurrentDependency(version: number): boolean {
        return !this.destroyed && this.dependencyVersion === version;
    }

    public async onFilterChanged(value: string, field: 'stocklocation_keyno_trolley' | 'consignmentshipment_keyno' | 'gb_consignment_status'): Promise<void> {
        if (!this.page) return;
        this.page[field] = value;
        await this.refreshResults();
    }

    public async onDateRangeChanged(value: DateModel): Promise<void> {
        if (!this.page) return;
        this.page.date_fom = value.date_fom;
        this.page.date_tom = value.date_tom;
        const index = this.intervals.findIndex((interval) => interval === value.dateselector_index);
        this.page.dateselector_index = index >= 0 ? String(index + 1) : value.dateselector_index;
        await this.refreshResults();
    }

    public async refreshResults(): Promise<void> {
        if (!this.page || !this.params || this.dependenciesLoading || this.destroyed) return;
        const page = this.page;
        if (!page.businessco_no_from?.trim() || !page.date_fom?.trim() || !page.date_tom?.trim() || !page.gb_consignment_status?.trim()
            || page.stockplace_id_from == null || page.stocklocation_keyno_trolley == null || page.consignmentshipment_keyno == null) return;
        const params: ConsignmentsListParams = {
            ...this.params, businessco_no_from: page.businessco_no_from, stockplace_id_from: page.stockplace_id_from,
            stocklocation_keyno_trolley: page.stocklocation_keyno_trolley, date_fom: page.date_fom, date_tom: page.date_tom,
            gb_consignment_status: page.gb_consignment_status, consignmentshipment_keyno: page.consignmentshipment_keyno,
        };
        const version = ++this.resultVersion;
        this.loadingList = false;
        this.resultParams = params;
        if (this.isDesktop) {
            if (!this.grid) {
                this.grid = new Grid().setRememberId('consignments.grid')
                    .setLoadDataFunction({ method: 3444, parameters: () => this.resultParams })
                    .setKendoConfig({ height: '100%', sortable: true, aggregate: true, pager: false, selectable: false, filterable: false });
            } else {
                this.grid.gridfunc?.read();
            }
            return;
        }
        this.loadingList = true;
        try {
            const consignments = await this.consignment.listConsignments(params);
            if (!this.destroyed && version === this.resultVersion) this.consignments = consignments;
        } finally {
            if (!this.destroyed && version === this.resultVersion) this.loadingList = false;
        }
    }

    public goToShipment(keyno: string): void {
        if (!this.page) return;
        this.navigator?.go({ to: 'consignmentshipment', parms: { consignmentshipment_keyno: keyno, stockplace_id: this.page.stockplace_id_from } });
    }

    public goToShipments(): void {
        this.navigator?.go({ to: 'consignmentshipments' });
    }

    public onConsignmentClick(event: { item: Item; event: MouseEvent }): void {
        if (!event.item.item_state) return;
        const target = { to: event.item.item_state, parms: event.item.item_parms };
        if (event.event.ctrlKey || event.event.metaKey) this.navigator?.newTab(target);
        else this.navigator?.go(target);
    }

    public ngOnDestroy(): void {
        this.destroyed = true;
        ++this.dependencyVersion;
        ++this.resultVersion;
        this.tabletQuery.removeEventListener('change', this.onBreakpointChanged);
    }

}
